/**
 * Kismet Quiz Validation Functions
 * Handles form validation for all quiz steps
 */

/**
 * Validate email format
 * @param {String} email - Email address to validate
 * @returns {Boolean} True if valid
 */
function isValidEmail(email) {
 if (!email || email.trim() === '') return false;
 const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
 return emailRegex.test(email);
}

/**
 * Validate required field
 * @param {*} value - Value to validate
 * @returns {Boolean} True if not empty
 */
function isValidRequired(value) {
 if (value === null || value === undefined) return false;
 if (typeof value === 'string') return value.trim() !== '';
 if (typeof value === 'number') return value !== 0;
 if (Array.isArray(value)) return value.length > 0;
 return !!value;
}

/**
 * Get validation errors for a specific quiz step
 * @param {Number} currentQuestion - Current step number (0-7)
 * @param {Object} currentDog - Current dog data
 * @param {Object} formData - Complete form data
 * @returns {Array} Array of error messages
 */
function getQuestionErrors(currentQuestion, currentDog, formData) {
 const formErrors = [];

 switch (currentQuestion) {
  case 0:
   // Step 1: User information
   if (!isValidRequired(formData.firstName)) {
    formErrors.push('First name is required');
   }
   if (!isValidRequired(formData.lastName)) {
    formErrors.push('Last name is required');
   }
   if (!isValidEmail(formData.email)) {
    formErrors.push('Valid email address is required');
   }
   break;

  case 1:
    // Step 2: Dog name and behavior
    const activeDogs = formData.dogs.slice(0, formData.dogCount || 1);
    
    if (activeDogs.some(dog => !dog.name || dog.name.trim() === '')) {
     formErrors.push('All dog names are required');
    }
    
    if (activeDogs.some(dog => !dog.behavior || dog.behavior === '')) {
     formErrors.push('All dog behaviors are required');
    }
    break;

  case 2:
   // Step 3: Breed and gender
   if (!isValidRequired(currentDog?.breed)) {
    formErrors.push('Dog breed is required');
   }
   if (!isValidRequired(currentDog?.gender)) {
    formErrors.push('Dog gender is required');
   }
   break;

  case 3:
   // Step 4: Birthday
   if (!isValidRequired(currentDog?.birthdayMonth)) {
    formErrors.push('Dog birthday month is required');
   }
   if (!isValidRequired(currentDog?.birthdayDay)) {
    formErrors.push('Dog birthday day is required');
   }
   if (!isValidRequired(currentDog?.birthdayYear)) {
    formErrors.push('Dog birthday year is required');
   }
   break;

  case 4:
   // Step 5: Weight and size
   if (!isValidRequired(currentDog?.weight) || parseFloat(currentDog?.weight || 0) === 0) {
    formErrors.push('Dog weight is required');
   }
   if (!isValidRequired(currentDog?.size)) {
    formErrors.push('Dog size is required');
   }
   break;

  case 5:
   // Step 6: Activity level
   if (!isValidRequired(currentDog?.activityLevel)) {
    formErrors.push('Dog activity level is required');
   }
   break;

  case 6:
   // Step 7: Support/health needs
   if (currentDog?.support) {
    const list = currentDog.support;
    if (!list || list.length === 0) {
     formErrors.push('Please select at least one support option');
    }
   } else {
    formErrors.push('Please select at least one support option');
   }
   break;

  case 7:
   // Step 8: Diet preferences
   if (currentDog?.diet) {
    if (!currentDog.diet || currentDog.diet === '') {
     formErrors.push('Please select a diet option');
    }
   } else {
    formErrors.push('Please select a diet option');
   }
   break;
 }

 return formErrors;
}

// Export for use in other scripts
if (typeof window !== 'undefined') {
 window.QuizValidator = {
  isValidEmail,
  isValidRequired,
  getQuestionErrors
 };
}
